import type { DomainConfig, DomainMainMenu, DomainMainMenuItem } from '@creator/sdk/modules/config/config.model';
import { getCreatorSdkConfig, getDomainName } from '@src/config';
import creatorSdk from '@src/services/creator-sdk';
import { deepMerge } from '@src/utils/object-utils/object-utils';
import { type Action, action, type Thunk, thunk } from 'easy-peasy';
import { initializeApp } from 'firebase/app';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { updateMenu } from './actions';
import { getDefaultDomainConfig, getStorageBucketFile, getStorageBucketFileUrl } from './helpers';

// Initialize Firebase for direct Firestore access
const firebaseConfig = getCreatorSdkConfig().firebaseConfig;

// Direct Firestore domain config loader
// const loadDomainConfigFromFirestore = async (domainName: string): Promise<DomainConfig | null> => {
//
// };

export interface ConfigModel {
    domainConfig: DomainConfig | null;
    mainMenu: DomainMainMenu | null;

    setDomainConfig: Action<ConfigModel, DomainConfig>;
    setDomainMainMenu: Action<ConfigModel, DomainMainMenu>;

    loadDomainConfig: Thunk<ConfigModel>;
    loadDomainMainMenu: Thunk<ConfigModel>;
    saveDomainConfig: Thunk<ConfigModel, DomainConfig>;
    updateMenu: Thunk<ConfigModel, DomainMainMenuItem[]>;
}

const configModel: ConfigModel = {
    domainConfig: null,
    mainMenu: null,

    loadDomainConfig: thunk(async actions => {
        const domainName = getDomainName();

        // Try SSR data first
        if (window.__SSR_DATA__.domainConfig) {
            console.log('[ConfigModel] Using SSR domain config');
            const defaultConfig = getDefaultDomainConfig();
            const newConfig = deepMerge<DomainConfig>(defaultConfig, window.__SSR_DATA__.domainConfig as DomainConfig);
            actions.setDomainConfig(newConfig);
            return newConfig;
        }

        // // Try Firestore directly
        // const firestoreConfig = await loadDomainConfigFromFirestore(domainName);
        // if (firestoreConfig) {
        //     const defaultConfig = getDefaultDomainConfig();
        //     const newConfig = deepMerge<DomainConfig>(defaultConfig, firestoreConfig);
        //     actions.setDomainConfig(newConfig);
        //     return newConfig;
        // }

        // Final fallback to SDK
        console.log('[ConfigModel] Falling back to SDK for domain config');
        // const config = await creatorSdk.configModule.getDomainConfig();
        const configUrl = await getStorageBucketFileUrl('config.json');
        const config = await fetch(configUrl)
            .then(res => res.json())
            .catch(() => null);
        const defaultConfig = getDefaultDomainConfig();
        const newConfig = deepMerge<DomainConfig>(defaultConfig, config as DomainConfig);
        actions.setDomainConfig(newConfig);
        return newConfig;
    }),

    loadDomainMainMenu: thunk(async actions => {
        const mainMenu = await creatorSdk.configModule.getDomainMainMenu();
        actions.setDomainMainMenu(mainMenu);
        return mainMenu;
    }),

    saveDomainConfig: thunk(async (actions, payload) => {
        try {
            console.log('[ConfigModel] Saving domain config to CloudRun...');

            // Get Firebase auth token
            const { getAuth } = await import('firebase/auth');
            const currentUser = getAuth().currentUser;
            if (!currentUser) {
                throw new Error('User not authenticated');
            }
            const authToken = await currentUser.getIdToken();

            // Get CloudRun base URL
            const { getCloudRunBaseUrl } = await import('@src/config');
            const cloudRunBaseUrl = getCloudRunBaseUrl();

            // Make the CloudRun API call
            const response = await fetch(`${cloudRunBaseUrl}/bbs-api/domain/config/update`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            // Update the local state
            actions.setDomainConfig(payload);
            console.log('[ConfigModel] Domain config saved successfully');
            return payload;
        } catch (error) {
            console.error('[ConfigModel] Error saving domain config:', error);
            throw error;
        }
    }),

    updateMenu: thunk(async (actions, payload) => {
        const menu = await updateMenu(payload);
        actions.setDomainMainMenu(menu);
    }),

    setDomainConfig: action((state, payload) => {
        state.domainConfig = payload;
    }),

    setDomainMainMenu: action((state, payload) => {
        state.mainMenu = payload;
    }),
};

export default configModel;
